import React, { useState, useEffect, useRef } from 'react';
import { 
  View, 
  Text, 
  StyleSheet, 
  Pressable, 
  ScrollView,
  Animated,
  Dimensions,
  Platform,
  ActivityIndicator
} from 'react-native';
import { theme as baseTheme } from '../styles/theme';
import { PRICING, FREE_TIER, STRIPE_CONFIG } from '../config/stripe';
import { useAuth } from '../hooks/useAuth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Navbar } from '../components/Navbar';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';

// Set page title
if (isWeb && typeof document !== 'undefined') {
  document.title = 'Plans - SheetShed';
}

// Responsive breakpoints
const getBreakpoint = (width) => {
  if (width < 480) return 'mobile';
  if (width < 768) return 'mobileLarge';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Staccato cursor
const StaccatoCursor = () => {
  const [position, setPosition] = useState({ x: -100, y: -100 });

  useEffect(() => {
    if (!isWeb) return;
    const handleMouseMove = (e) => setPosition({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handleMouseMove);
    document.body.style.cursor = 'none';
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.body.style.cursor = 'auto';
    };
  }, []);

  if (!isWeb) return null;

  return (
    <div style={{
      position: 'fixed', left: position.x - 4, top: position.y - 4,
      width: 8, height: 8, borderRadius: '50%', backgroundColor: '#C75B7A',
      pointerEvents: 'none', zIndex: 10000, boxShadow: '0 0 12px rgba(199, 91, 122, 0.5)',
    }} />
  );
};

// Animated text
const AnimatedText = ({ children, style, delay = 0 }) => {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(30)).current;

  useEffect(() => {
    const timer = setTimeout(() => {
      Animated.parallel([
        Animated.timing(opacity, { toValue: 1, duration: 600, useNativeDriver: true }),
        Animated.spring(translateY, { toValue: 0, friction: 12, tension: 60, useNativeDriver: true }),
      ]).start();
    }, delay);
    return () => clearTimeout(timer);
  }, []);

  return <Animated.Text style={[style, { opacity, transform: [{ translateY }] }]}>{children}</Animated.Text>;
};

// Animated section
const AnimatedSection = ({ children, delay = 0, style }) => {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(40)).current;
  const scale = useRef(new Animated.Value(0.96)).current;

  useEffect(() => {
    const timer = setTimeout(() => {
      Animated.parallel([
        Animated.timing(opacity, { toValue: 1, duration: 600, useNativeDriver: true }),
        Animated.spring(translateY, { toValue: 0, friction: 10, tension: 50, useNativeDriver: true }),
        Animated.spring(scale, { toValue: 1, friction: 10, tension: 40, useNativeDriver: true }),
      ]).start();
    }, delay);
    return () => clearTimeout(timer);
  }, []);

  return <Animated.View style={[style, { opacity, transform: [{ translateY }, { scale }] }]}>{children}</Animated.View>;
};

// Button with hover
const AnimatedButton = ({ children, onPress, style, disabled, theme }) => {
  const scale = useRef(new Animated.Value(1)).current;

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      onPressIn={() => Animated.spring(scale, { toValue: 0.96, useNativeDriver: true }).start()}
      onPressOut={() => Animated.spring(scale, { toValue: 1, useNativeDriver: true }).start()}
      onHoverIn={() => Animated.spring(scale, { toValue: 1.03, useNativeDriver: true }).start()}
      onHoverOut={() => Animated.spring(scale, { toValue: 1, useNativeDriver: true }).start()}
    >
      <Animated.View style={[style, { transform: [{ scale }] }]}>{children}</Animated.View>
    </Pressable>
  );
};

// Check mark
const CheckIcon = ({ color }) => (
  <View style={[styles.checkIcon, { backgroundColor: color }]}>
    <View style={styles.checkMarkIcon} />
  </View>
);

// Pricing card
const PricingCard = ({ plan, isPopular, onSelect, loading, theme, screenWidth, delay }) => {
  const scale = useRef(new Animated.Value(1)).current;
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(50)).current;

  const breakpoint = getBreakpoint(screenWidth);
  const isMobile = breakpoint === 'mobile' || breakpoint === 'mobileLarge';

  useEffect(() => {
    const timer = setTimeout(() => {
      Animated.parallel([
        Animated.timing(opacity, { toValue: 1, duration: 600, useNativeDriver: true }),
        Animated.spring(translateY, { toValue: 0, friction: 10, tension: 50, useNativeDriver: true }),
      ]).start();
    }, delay);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Pressable
      onHoverIn={() => Animated.spring(scale, { toValue: 1.02, useNativeDriver: true }).start()}
      onHoverOut={() => Animated.spring(scale, { toValue: 1, useNativeDriver: true }).start()}
      onPress={() => onSelect(plan)}
    >
      <Animated.View style={[styles.pricingCard, { 
        backgroundColor: isPopular ? theme.surfaceDark : theme.surface,
        opacity, transform: [{ translateY }, { scale }],
        borderWidth: isPopular ? 0 : 1, borderColor: theme.border,
        padding: isMobile ? 28 : 36,
        minWidth: isMobile ? '100%' : 320,
      }]}>
        {plan.badge && (
          <View style={[styles.badge, { backgroundColor: theme.primary }]}>
            <Text style={[styles.badgeText, { color: theme.textOnPrimary }]}>{plan.badge}</Text>
          </View>
        )}

        <Text style={[styles.planName, { color: isPopular ? theme.textOnDark : theme.textPrimary, fontSize: isMobile ? 22 : 26 }]}>{plan.name}</Text>

        <View style={styles.priceContainer}>
          <Text style={[styles.currency, { color: isPopular ? theme.textOnDarkMuted : theme.textMuted }]}>$</Text>
          <Text style={[styles.price, { color: isPopular ? theme.textOnDark : theme.textPrimary, fontSize: isMobile ? 48 : 60 }]}>{plan.price.toFixed(2).split('.')[0]}</Text>
          <Text style={[styles.cents, { color: isPopular ? theme.textOnDark : theme.textPrimary, fontSize: isMobile ? 22 : 26 }]}>.{plan.price.toFixed(2).split('.')[1]}</Text>
          {plan.interval && <Text style={[styles.interval, { color: isPopular ? theme.textOnDarkMuted : theme.textMuted }]}>/{plan.interval === 'month' ? 'mo' : 'yr'}</Text>}
        </View>

        <Text style={[styles.planDescription, { color: isPopular ? theme.textOnDarkMuted : theme.textSecondary, fontSize: isMobile ? 15 : 16 }]}>{plan.description}</Text>

        <View style={styles.featuresList}>
          {plan.features.map((feature, idx) => (
            <View key={idx} style={styles.featureItem}>
              <CheckIcon color={theme.primary} />
              <Text style={[styles.featureText, { color: isPopular ? theme.textOnDark : theme.textPrimary, fontSize: isMobile ? 15 : 16 }]}>{feature}</Text>
            </View>
          ))}
        </View>

        <AnimatedButton onPress={() => onSelect(plan)} disabled={loading} style={[styles.selectButton, { backgroundColor: isPopular ? theme.primary : theme.backgroundAlt }]} theme={theme}>
          {loading ? (
            <ActivityIndicator color={isPopular ? theme.textOnPrimary : theme.textPrimary} />
          ) : (
            <Text style={[styles.selectButtonText, { color: isPopular ? theme.textOnPrimary : theme.textPrimary, fontSize: isMobile ? 16 : 17 }]}>
              {plan.type === 'one_time' ? 'Get Lifetime Access' : 'Subscribe'}
            </Text>
          )}
        </AnimatedButton>
      </Animated.View>
    </Pressable>
  );
};

// Free tier card
const FreeTierCard = ({ theme, onSignUp, screenWidth, delay }) => {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(40)).current;
  const scale = useRef(new Animated.Value(1)).current;

  const breakpoint = getBreakpoint(screenWidth);
  const isMobile = breakpoint === 'mobile' || breakpoint === 'mobileLarge';

  useEffect(() => {
    const timer = setTimeout(() => {
      Animated.parallel([
        Animated.timing(opacity, { toValue: 1, duration: 600, useNativeDriver: true }),
        Animated.spring(translateY, { toValue: 0, friction: 10, tension: 50, useNativeDriver: true }),
      ]).start();
    }, delay);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Pressable
      onHoverIn={() => Animated.spring(scale, { toValue: 1.02, useNativeDriver: true }).start()}
      onHoverOut={() => Animated.spring(scale, { toValue: 1, useNativeDriver: true }).start()}
    >
      <Animated.View style={[styles.freeTierCard, { 
        backgroundColor: theme.surface, borderColor: theme.border, opacity, transform: [{ translateY }, { scale }],
        padding: isMobile ? 28 : 36, minWidth: isMobile ? '100%' : 320,
      }]}>
        {/* Content section */}
        <View style={styles.freeTierContent}>
          <Text style={[styles.freeTierTitle, { color: theme.textPrimary, fontSize: isMobile ? 22 : 26 }]}>Free Forever</Text>
          <View style={styles.freePriceRow}>
            <Text style={[styles.freePrice, { color: theme.textPrimary, fontSize: isMobile ? 48 : 60 }]}>$0</Text>
          </View>
          <Text style={[styles.freeTierSubtitle, { color: theme.textSecondary, fontSize: isMobile ? 15 : 16 }]}>Get started with generous limits</Text>
          <View style={styles.freeTierFeatures}>
            {FREE_TIER.features.map((feature, idx) => (
              <View key={idx} style={styles.freeTierFeatureItem}>
                <View style={[styles.checkMarkIcon, { borderColor: theme.success }]} />
                <Text style={[styles.freeTierFeatureText, { color: theme.textSecondary, fontSize: isMobile ? 14 : 15 }]}>{feature}</Text>
              </View>
            ))}
          </View>
        </View>
        {/* Button anchored to bottom */}
        <AnimatedButton onPress={onSignUp} style={[styles.freeTierButton, { borderColor: theme.border }]} theme={theme}>
          <Text style={[styles.freeTierButtonText, { color: theme.textPrimary, fontSize: isMobile ? 16 : 17 }]}>Start Free</Text>
        </AnimatedButton>
      </Animated.View>
    </Pressable>
  );
};

// Stripe.js singleton — only instantiated once
let stripePromise;
const getStripe = () => {
  if (!stripePromise) stripePromise = loadStripe(STRIPE_CONFIG.publishableKey);
  return stripePromise;
};

export const PlansScreen = ({ navigation }) => {
  const { user } = useAuth();
  const [pricingMode, setPricingMode] = useState('LTD');
  const [firestorePlans, setFirestorePlans] = useState(null); // null = not loaded yet
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [screenWidth, setScreenWidth] = useState(SCREEN_WIDTH);
  const [isDark, setIsDark] = useState(false);

  // Load dark mode preference
  useEffect(() => {
    if (isWeb && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('sheetshed_darkmode');
      if (saved === 'true') setIsDark(true);
    }
  }, []);

  // Save dark mode preference
  const toggleDarkMode = (value) => {
    const newValue = typeof value === 'boolean' ? value : !isDark;
    setIsDark(newValue);
    if (isWeb && typeof localStorage !== 'undefined') {
      localStorage.setItem('sheetshed_darkmode', String(newValue));
    }
  };

  const theme = isDark ? baseTheme.dark : baseTheme.light;
  const breakpoint = getBreakpoint(screenWidth);
  const isMobile = breakpoint === 'mobile' || breakpoint === 'mobileLarge';

  useEffect(() => {
    if (!isWeb) return;
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load pricing config from Firestore (falls back to hardcoded PRICING)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [appSnap, plansSnap] = await Promise.all([
          getDoc(doc(db, 'settings', 'app')),
          getDoc(doc(db, 'settings', 'plans')),
        ]);
        if (cancelled) return;
        const appData = appSnap.exists() ? appSnap.data() : {};
        const plansData = plansSnap.exists() ? plansSnap.data() : null;
        if (appData.pricingMode) setPricingMode(appData.pricingMode);
        if (plansData) setFirestorePlans(plansData);
      } catch (e) {
        // Firestore unavailable — fall back to hardcoded PRICING
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Detect Stripe redirect back after successful payment → navigate to WelcomeScreen
  useEffect(() => {
    if (!isWeb || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      window.history.replaceState({}, '', window.location.pathname);
      navigation.replace('Welcome');
    }
  }, []);

  const handleSelectPlan = async (plan) => {
    if (!user) {
      navigation.navigate('Auth', { mode: 'signup', selectedPlan: plan.id });
      return;
    }
    setCheckoutError(null);
    setLoading(true);
    try {
      const origin = isWeb && typeof window !== 'undefined' ? window.location.origin : 'https://sheetshed.app';
      const functions = getFunctions();
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      const result = await createCheckoutSession({
        priceId: plan.id,
        successUrl: `${origin}/plans?payment=success`,
        cancelUrl: `${origin}/plans?payment=cancelled`,
      });
      const { url } = result.data;
      if (url && isWeb && typeof window !== 'undefined') {
        window.location.href = url;
      } else {
        setCheckoutError('Checkout unavailable on this platform. Please visit sheetshed.app to purchase.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setCheckoutError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = () => navigation.navigate('Auth', { mode: 'signup' });

  const getPadding = () => {
    if (breakpoint === 'mobile') return 20;
    if (breakpoint === 'mobileLarge') return 28;
    if (breakpoint === 'tablet') return 40;
    return 60;
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.background }]}>
      <StaccatoCursor />

      <Navbar 
        navigation={navigation} 
        isDark={isDark} 
        toggleDarkMode={toggleDarkMode} 
        currentRoute="Plans"
        screenWidth={screenWidth} 
      />

      {/* Payment success banner */}

      {/* Checkout error banner */}
      {checkoutError && (
        <View style={[styles.errorBanner, { backgroundColor: theme.error || '#ef4444' }]}>
          <Text style={styles.errorBannerText}>{checkoutError}</Text>
          <Pressable onPress={() => setCheckoutError(null)} style={styles.errorBannerDismiss}>
            <Text style={styles.errorBannerDismissText}>✕</Text>
          </Pressable>
        </View>
      )}

      <ScrollView style={styles.scrollView} contentContainerStyle={[styles.scrollContent, { paddingHorizontal: getPadding() }]} showsVerticalScrollIndicator={false}>
        {/* Title Section */}
        <AnimatedSection style={styles.titleSection} delay={0}>
          <AnimatedText style={[styles.pageTitle, { color: theme.textPrimary, fontSize: isMobile ? 32 : 52 }]} delay={100}>
            Simple, transparent pricing
          </AnimatedText>
          <AnimatedText style={[styles.pageSubtitle, { color: theme.textSecondary, fontSize: isMobile ? 17 : 20 }]} delay={200}>
            {pricingMode === 'LTD' ? 'Limited time offer for early adopters' : 'Choose the plan that works for you'}
          </AnimatedText>
        </AnimatedSection>

        {/* Billing Toggle (subscription mode) */}
        {pricingMode === 'SUBSCRIPTION' && (
          <AnimatedSection style={styles.billingToggleContainer} delay={250}>
            <View style={[styles.billingToggle, { backgroundColor: theme.backgroundAlt }]}>
              <AnimatedButton onPress={() => setBillingCycle('monthly')} style={[styles.toggleOption, billingCycle === 'monthly' && { backgroundColor: theme.primary }]} theme={theme}>
                <Text style={[styles.toggleText, { color: billingCycle === 'monthly' ? theme.textOnPrimary : theme.textSecondary }]}>Monthly</Text>
              </AnimatedButton>
              <AnimatedButton onPress={() => setBillingCycle('yearly')} style={[styles.toggleOption, billingCycle === 'yearly' && { backgroundColor: theme.primary }]} theme={theme}>
                <Text style={[styles.toggleText, { color: billingCycle === 'yearly' ? theme.textOnPrimary : theme.textSecondary }]}>Yearly</Text>
                <View style={[styles.saveBadge, { backgroundColor: theme.success }]}>
                  <Text style={styles.saveBadgeText}>Save 30%</Text>
                </View>
              </AnimatedButton>
            </View>
          </AnimatedSection>
        )}

        {/* Pricing Cards - HORIZONTAL LAYOUT (Free first, then Pro) */}
        <View style={[styles.pricingContainer, { flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 24 : 32, alignItems: 'stretch' }]}>
          <FreeTierCard theme={theme} onSignUp={handleSignUp} screenWidth={screenWidth} delay={300} />
          {pricingMode === 'LTD' ? (
            <PricingCard
              plan={firestorePlans?.LTD ?? PRICING.LTD}
              isPopular={true}
              onSelect={handleSelectPlan}
              loading={loading}
              theme={theme}
              screenWidth={screenWidth}
              delay={400}
            />
          ) : (
            <PricingCard
              plan={firestorePlans?.MONTHLY ?? PRICING.MONTHLY}
              isPopular={true}
              onSelect={handleSelectPlan}
              loading={loading}
              theme={theme}
              screenWidth={screenWidth}
              delay={400}
            />
          )}
        </View>

        {pricingMode === 'LTD' && (
          <AnimatedSection style={styles.ltdTimer} delay={500}>
            <Text style={[styles.ltdTimerText, { color: theme.textMuted, fontSize: isMobile ? 14 : 16 }]}>⏰ Limited time founding offer</Text>
          </AnimatedSection>
        )}

        {/* FAQ Section */}
        <AnimatedSection style={styles.faqSection} delay={600}>
          <AnimatedText style={[styles.faqTitle, { color: theme.textPrimary, fontSize: isMobile ? 28 : 36 }]} delay={650}>Questions?</AnimatedText>
          
          {[
            { q: 'What happens when I reach 20 sheets on free?', a: 'You can still view all your sheets. To upload more, simply delete older ones or upgrade to Pro for unlimited storage.' },
            { q: 'Can I cancel anytime?', a: pricingMode === 'LTD' ? "The lifetime deal is a one-time purchase with no recurring charges. It's yours forever!" : "Yes! Cancel anytime from your account settings. You'll keep access until the end of your billing period." },
            { q: 'Is my data secure?', a: 'Absolutely. Your sheets are stored securely and only accessible by you. We use industry-standard encryption.' }
          ].map((faq, idx) => (
            <AnimatedSection key={idx} style={[styles.faqCard, { backgroundColor: theme.surface }]} delay={700 + idx * 100}>
              <Text style={[styles.faqQuestion, { color: theme.textPrimary, fontSize: isMobile ? 16 : 18 }]}>{faq.q}</Text>
              <Text style={[styles.faqAnswer, { color: theme.textSecondary, fontSize: isMobile ? 14 : 16 }]}>{faq.a}</Text>
            </AnimatedSection>
          ))}
        </AnimatedSection>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={[styles.footerText, { color: theme.textMuted, fontSize: isMobile ? 14 : 15 }]}>🔒 Secure payments powered by Stripe</Text>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  scrollView: { flex: 1 },
  scrollContent: { paddingTop: 100, paddingBottom: 80 },

  // Floating Navbar
  navbarContainer: { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 100, paddingTop: 20 },
  navbar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderRadius: 999, paddingHorizontal: 20, paddingVertical: 14 },
  logoContainer: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  logoIcon: { width: 40, height: 40, borderRadius: 14, justifyContent: 'center', alignItems: 'center', position: 'relative' },
  logoNote: { width: 9, height: 9, borderRadius: 5, position: 'absolute', top: 11, left: 11 },
  logoNote2: { width: 7, height: 7, borderRadius: 4, position: 'absolute', bottom: 11, right: 11 },
  logoText: { fontSize: 20, fontWeight: '700' },
  navLinks: { flexDirection: 'row', gap: 8 },
  navLink: { paddingHorizontal: 16, paddingVertical: 10, borderRadius: 999 },
  navLinkActive: { borderRadius: 999 },
  navLinkText: { fontSize: 15, fontWeight: '500' },
  navRight: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  darkModeToggle: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  navButton: { paddingHorizontal: 20, paddingVertical: 12, borderRadius: 999 },
  navButtonText: { fontSize: 15, fontWeight: '600' },
  userAvatar: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  userAvatarText: { fontSize: 16, fontWeight: '700' },

  // Title
  titleSection: { alignItems: 'center', paddingVertical: 60 },
  pageTitle: { fontWeight: '700', textAlign: 'center', marginBottom: 20 },
  pageSubtitle: { textAlign: 'center', maxWidth: 450 },

  // Billing Toggle
  billingToggleContainer: { alignItems: 'center', marginBottom: 40 },
  billingToggle: { flexDirection: 'row', borderRadius: 999, padding: 5 },
  toggleOption: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 24, paddingVertical: 14, borderRadius: 999 },
  toggleText: { fontSize: 16, fontWeight: '600' },
  saveBadge: { paddingHorizontal: 10, paddingVertical: 3, borderRadius: 999 },
  saveBadgeText: { fontSize: 11, fontWeight: '700', color: '#fff' },

  // Pricing Container - HORIZONTAL
  pricingContainer: { justifyContent: 'center', alignItems: 'center', maxWidth: 1000, alignSelf: 'center', width: '100%' },
  ltdTimer: { alignItems: 'center', marginTop: 24 },
  ltdTimerText: {},

  // Pricing Card
  pricingCard: { borderRadius: 32, flex: 1, ...Platform.select({ web: { boxShadow: '0 8px 40px rgba(0,0,0,0.08)' } }) },
  badge: { position: 'absolute', top: -14, left: 28, paddingHorizontal: 18, paddingVertical: 8, borderRadius: 999 },
  badgeText: { fontSize: 13, fontWeight: '700' },
  planName: { fontWeight: '700', marginBottom: 20, marginTop: 10 },
  priceContainer: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12 },
  currency: { fontSize: 22, marginTop: 10 },
  price: { fontWeight: '700', lineHeight: 60 },
  cents: { fontWeight: '700', marginTop: 10 },
  interval: { fontSize: 18, marginTop: 24, marginLeft: 4 },
  planDescription: { marginBottom: 28 },
  featuresList: { gap: 14, marginBottom: 36 },
  featureItem: { flexDirection: 'row', alignItems: 'center', gap: 14 },
  checkIcon: { width: 22, height: 22, borderRadius: 11, justifyContent: 'center', alignItems: 'center' },
  checkMark: { color: '#fff', fontSize: 13, fontWeight: '700' },
  checkMarkIcon: { width: 14, height: 8, borderLeftWidth: 2, borderBottomWidth: 2, borderColor: '#fff', transform: [{ rotate: '-45deg' }], marginRight: 4 },
  featureText: { flex: 1 },
  selectButton: { paddingVertical: 20, borderRadius: 999, alignItems: 'center' },
  selectButtonText: { fontWeight: '600' },

  // Free Tier Card - with button anchored to bottom
  freeTierCard: { borderRadius: 28, borderWidth: 1, flex: 1, justifyContent: 'space-between' },
  freeTierContent: { flex: 1 },
  freeTierTitle: { fontWeight: '700', marginBottom: 20, marginTop: 10 },
  freePriceRow: { marginBottom: 12 },
  freePrice: { fontWeight: '700' },
  freeTierSubtitle: { marginBottom: 28 },
  freeTierFeatures: { gap: 14, marginBottom: 36 },
  freeTierFeatureItem: { flexDirection: 'row', alignItems: 'center', gap: 14 },
  freeTierCheck: { fontSize: 15, fontWeight: '700' },
  freeTierFeatureText: {},
  freeTierButton: { paddingVertical: 20, borderRadius: 999, alignItems: 'center', borderWidth: 2, marginTop: 'auto' },
  freeTierButtonText: { fontWeight: '600' },

  // FAQ
  faqSection: { paddingVertical: 60, maxWidth: 700, alignSelf: 'center', width: '100%' },
  faqTitle: { fontWeight: '700', marginBottom: 32, textAlign: 'center' },
  faqCard: { borderRadius: 24, padding: 28, marginBottom: 16 },
  faqQuestion: { fontWeight: '600', marginBottom: 12 },
  faqAnswer: { lineHeight: 26 },

  // Footer
  footer: { alignItems: 'center', paddingVertical: 32 },
  footerText: {},

  // Payment banners
  successBanner: { padding: 20, alignItems: 'center', gap: 12 },
  successBannerText: { color: '#fff', fontSize: 16, fontWeight: '600', textAlign: 'center' },
  successBannerBtn: { backgroundColor: 'rgba(255,255,255,0.25)', paddingHorizontal: 20, paddingVertical: 10, borderRadius: 999 },
  successBannerBtnText: { color: '#fff', fontWeight: '700', fontSize: 15 },
  errorBanner: { padding: 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  errorBannerText: { color: '#fff', fontSize: 14, flex: 1 },
  errorBannerDismiss: { padding: 8 },
  errorBannerDismissText: { color: '#fff', fontWeight: '700' },
});

export default PlansScreen;
